import { NgClass } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { SUB_STATUS } from '@core/models/admin/admin-account.types';
import { TRANSLATION_KEYS } from '@core/internationalization/translations_keys';
import { AccountService } from '@core/services/admin/account.service';
import { StripeInternalHttpService } from '@core/services/admin/stripe-internal.http.service';
import { APP_SLUGS, MAIN_SLUGS, SETTINGS_SLUGS } from '@routes/routes.types';
import { TwinnyBrandComponent } from '@shared/components/twinny-brand/twinny-brand.component';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { AlertTriangle, LucideAngularModule } from 'lucide-angular';
import { ToastrService } from 'ngx-toastr';
import { filter, map } from 'rxjs';

import { BILLING_SLUGS } from '@features/settings/billing/billing.routes';
import { BillingService } from '@features/settings/billing/services/billing.service';
import { SIDEBAR_OPTS } from './sidebar.config';

@Component({
    selector: 'app-sidebar',
    imports: [NgClass, TranslatePipe, RouterModule, TwinnyBrandComponent, LucideAngularModule],
    templateUrl: './sidebar.component.html',
})
export class SidebarComponent implements OnInit {
    private readonly router = inject(Router);
    private readonly translateS = inject(TranslateService);
    private readonly billingS = inject(BillingService);
    private readonly accountS = inject(AccountService);
    private readonly stripeInternalHttpS = inject(StripeInternalHttpService);
    private readonly toastr = inject(ToastrService);

    protected readonly SLUGS = MAIN_SLUGS;
    protected readonly OPTS = SIDEBAR_OPTS(this.translateS);
    protected readonly WarningIcon = AlertTriangle;
    private readonly analyticsv2 = ['listening', 'users'];

    private readonly lastSlug = signal('');

    protected readonly isAnalytics = computed(() => this.analyticsv2.includes(this.lastSlug()));

    protected readonly BILLING_TRANSLATION_KEYS = TRANSLATION_KEYS.settings.billing;
    readonly daysUntilDue = this.billingS.daysUntilDue;
    readonly periodEnd = this.billingS.periodEnd;
    readonly subStatus = this.billingS.subStatus;

    readonly trialText = computed(() => {
        const isExpired = this.subStatus() === SUB_STATUS.INCOMPLETE_EXPIRED;
        if (isExpired) {
            return this.translateS.instant(this.BILLING_TRANSLATION_KEYS.warning.expired);
        } else {
            const daysUntilDue = this.daysUntilDue()?.toString() ?? '';
            return this.translateS
                .instant(this.BILLING_TRANSLATION_KEYS.warning.nearExpiry)
                .replace('{periodEnd}', this.periodEnd())
                .replace('{daysUntilDue}', daysUntilDue)
                .replace('Growth plan', 'Free Plan');
        }
    });

    readonly showTrialWarning = computed(() => this.subStatus() === SUB_STATUS.TRIAL);

    // -- Custom (ad-hoc) pricing ---------------------------------------------
    // Same source of truth as settings/account & user-menu: a negotiated budget
    // plan carries `custom_price_ids`.
    private readonly customPriceIds = computed(
        () => this.accountS.account()?.subscription?.custom_price_ids ?? []
    );
    private readonly hasCustomPricing = computed(() => this.customPriceIds().length > 0);

    /**
     * Mirrors settings/account's `handleSubscribeNow()` (and user-menu's
     * `handleUpgrade()`) so the trial Subscribe button always behaves
     * identically: custom-budget plans check out directly via the *hosted*
     * Stripe checkout, everyone else routes to the subscribe wizard — never the
     * `billingS.goToSubcribe()` branch (which opens a modal for some states).
     */
    handleSubscribe() {
        if (this.hasCustomPricing()) {
            this.stripeInternalHttpS.initCheckout(this.customPriceIds(), true).subscribe({
                next: url => (window.location.href = url),
                error: (err: HttpErrorResponse) =>
                    this.toastr.error(err?.error?.message || 'Failed to initiate checkout'),
            });
            return;
        }

        this.router.navigateByUrl(
            `/${APP_SLUGS.MAIN}/${MAIN_SLUGS.SETTINGS}/${SETTINGS_SLUGS.billing}/${BILLING_SLUGS.subscribe}`
        );
    }

    ngOnInit() {
        this.router.events
            .pipe(
                filter((event: any) => event instanceof NavigationEnd || 'routerEvent' in event),
                map((event: any) => event?.url || event?.routerEvent!.url)
            )
            .subscribe(url => {
                const lastUrlSegment = url.split('?')[0].split('/').pop();
                this.lastSlug.set(lastUrlSegment);
            });
    }
}
